import React, { useState, useEffect, useMemo, useRef } from "react";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { getFormValues, getFormSyncErrors, isDirty, touch } from "redux-form";
import { closeCreateNewCustomerModal } from "actions/ui";
import { isEmpty, get } from "lodash";
import cx from "classnames";
import baseService from "services/BaseService";
import Loader from "../common/Loader";
import CustomerForm from "./CustomerForm";
import MessageModal from "../common/MessageModal";
import { ModalWindow } from "ui-core-dashboard";
import DynamicForm from "../common/DynamicForm";
import SearchPreview from "./SearchPreview";
import { addCustomerToAppeal, getAppealFeedback } from "modules/appeal/actions";
import { getContactPerson, getCustomer } from "actions/customer";
import { getFormColumns, getCustomerCreateParamsData } from "./config";
import { getModalFormDataFromState } from "helpers";

import useDebouncedPreviewValues from "./SearchPreview/useDebouncedPreviewValues";

import "./styles.scss";

const STATIC_TYPE_FORM_NAME = "create-new-customer-type-modal-form";

const INIT_SEARCH_DATA = { result: [], total: 0, isLoading: false };
const DEBOUNCE_TIMEOUT = 500;
const MODAL_TRANSITION_DURATION = 800;

const mapStateToProps = state => {
    return {
        formTypeErrors: getFormSyncErrors(STATIC_TYPE_FORM_NAME)(state),
        formTypeValues: getFormValues(STATIC_TYPE_FORM_NAME)(state),
        form: state.form,
        newCustomerModalContext: get(state, "ui.newCustomerModalContext"),
        incomePhoneNumber: get(state, "call.incomePhoneNumber")
    };
};

const mapDispatchToProps = {
    closeCreateNewCustomerModal,
    addCustomerToAppeal,
    getContactPerson,
    getCustomer,
    getAppealFeedback,
    touch,
};

const CreateNewCustomerModal = props => {
    const {
        t,
        isOpen,
        onClose,
        onCustomerSave,
        formTypeErrors,
        formTypeValues,
        history,
        newCustomerModalContext = {},
        closeCreateNewCustomerModal,
        form,
        touch,
        incomePhoneNumber
    } = props;

    const modalRef = useRef(null);
    // const formRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [values, setValues] = useState(null);

    const [isForceHiddenController, setIsForceHiddenController] = useState(false);
    const [isCustomerTypesLoading, setIsCustomerTypesLoading] = useState(false);
    const [customerTypes, setCustomerTypes] = useState([]);

    const [searchData, setSearchData] = useState(INIT_SEARCH_DATA);

    const [isWarningPopupOpen, setIsWarningPopupOpen] = useState(false);

    const [isSomeFormDirty, setIsSomeFormDirty] = useState(false);

    const selectedCustomerType = useMemo(() => {
        const selectedTypeId = formTypeValues && formTypeValues.type;
        if (selectedTypeId) {
            const selectedType = customerTypes.find(type => type.id === selectedTypeId);
            return selectedType;
        }
        if (newCustomerModalContext.partyType) {
            return {
                customerPartyType: newCustomerModalContext.customerPartyType,
                partyType: newCustomerModalContext.partyType,
                formCode: newCustomerModalContext.formCode,
                id: newCustomerModalContext.partyId,
            };
        }
        return null;
    }, [formTypeValues, customerTypes]);

    const modalFormData = useMemo(() => {
        if (selectedCustomerType) {
            const modalFormData = getModalFormDataFromState(selectedCustomerType && selectedCustomerType.formCode);
            return modalFormData;
        }
        return null;
    }, [selectedCustomerType]);


    // we need to provide a selected customer data from form (for example when creating a new contact person)
    const selectedCustomer = useMemo(() => {
        return values && values.customer
    }, [values]);

    const formColumns = useMemo(() => {
        if (modalFormData) {
            const columns = getFormColumns(t, selectedCustomerType, modalFormData, incomePhoneNumber, newCustomerModalContext.customerId, selectedCustomer);
            return columns.filter(column => column && Array.isArray(column) && column.length); // filter empty hidden: true columns (empty arrays)
        }
        return null;
    }, [modalFormData, selectedCustomerType, selectedCustomer]);

    const initCustomerTypeForm = useMemo(() => {
        const defaultType = customerTypes.find(item => item.isDefault) || customerTypes[0];
        const widgets = [
            {
                key: "type",
                title: t("type"),
                clearable: true,
                disabled: false,
                savedValue: defaultType ? defaultType.id : "",
                values: customerTypes.map(item => ({ key: item.id, value: item.partyName })),
                isReq: true,
                clearable: false,
                customWidth: "full-width",
                widgetType: "select",
            },
        ];
        return [
            {
                view: "1column",
                collapsed: false,
                key: "customer-new-address",
                widgets: widgets,
            },
        ];
    }, [customerTypes]);

    const staticSearchPreviewBlock = useMemo(() => {
        return modalFormData && modalFormData.searchPreview;
    }, [modalFormData]);

    const staticSearchSearchAttrs = useMemo(() => {
        if (!modalFormData) {
            return [];
        }
        const mergedAttrs = Object.keys(modalFormData).flatMap(key => {
            return modalFormData[key].sortedAttrs;
        });
        return mergedAttrs.filter(attr => attr.searchPreview);
    }, [modalFormData]);

    const { debouncedPreviewValues, setDebouncedPreviewValues, isSearchDataChecked } = useDebouncedPreviewValues(
        values,
        staticSearchSearchAttrs,
        2,
        DEBOUNCE_TIMEOUT,
        searchData
    );

    const isSearchPreviewEnabled = staticSearchPreviewBlock && !staticSearchPreviewBlock.hidden;

    const isSearchPreview = isSearchPreviewEnabled && !isEmpty(debouncedPreviewValues);

    const isSubmitDisabledBySearchPreview =
    isSearchPreviewEnabled && (staticSearchSearchAttrs.length > 0 && !isSearchDataChecked) || searchData.isLoading || searchData.total > 0;

    // console.log({isSubmitDisabledBySearchPreview, staticSearchSearchAttrs, isSearchDataChecked, searchData})

    async function fetchCustomerTypes() {
        setIsCustomerTypesLoading(true);
        try {
            const response = await baseService.get("customer_types", {
                // data: { objectType, objectId: customerId },
            });
            if (response && response.success) {
                setCustomerTypes(response.result);
            }
        } catch (e) {
            console.error("CustomerForm::fetchCustomerTypes: Error", e);
        } finally {
            setIsCustomerTypesLoading(false);
        }
    }

    function handleOpenWarningPopup() {
        setIsWarningPopupOpen(true);
    }

    function handleCloseWarningPopup() {
        setIsWarningPopupOpen(false);
    }

    function handleClose(isIgnoreDirty, isCustomerCreated) {
        if (!isIgnoreDirty && isSomeFormDirty) {
            handleOpenWarningPopup();
            return;
        }
        if (onClose && typeof onClose === "function") {
            onClose(isIgnoreDirty, isCustomerCreated);
        }
        if (
            newCustomerModalContext &&
            newCustomerModalContext.onClose &&
            typeof newCustomerModalContext.onClose === "function"
        ) {
            newCustomerModalContext.onClose(isIgnoreDirty, isCustomerCreated);
        }
        closeCreateNewCustomerModal();
    }

    // set search data and force hide FieldController during search preview transition
    function handleSearchDataUpdate(data) {
        if ((searchData.isHidden && !data.isHidden) || (data.isHidden && !searchData.isHidden)) {
            setIsForceHiddenController(true);
            setTimeout(() => setIsForceHiddenController(false), MODAL_TRANSITION_DURATION);
        }
        setSearchData(data);
    }

    async function handleCardSelect(data) {
        const { type } = data;
        let customerId = data.id;
        let contactPersonId;
        if (type === "contact_person") {
            customerId = data.customerId;
            contactPersonId = data.id;
        }
        if (newCustomerModalContext.openNewTab) {
            if (type === "contact_person") {
                // contact person /customer/6701607/person/82313/appeals
                history.push(`/customer/${customerId}/person/${contactPersonId}/appeals`);
            } else {
                // others /customer/6701606/appeals
                history.push(`/customer/${customerId}/appeals`);
            }
        }
        // handle adding customer to appeal
        if (!newCustomerModalContext.openNewTab) {
            if (isLoading) {
                return;
            }
            setIsLoading(true);
            const path = props.location.pathname;
            const splittedPath = path.split("/");
            const appealsIndex = splittedPath.findIndex(item => item === "appeals");
            const appealId = splittedPath[appealsIndex + 1];
            const callback = get(newCustomerModalContext, "callback");
            let isCallbackSuccess = true; // set to true for cases without callback provided (is there such cases?)
            if (typeof callback === "function") {
                isCallbackSuccess = await callback(customerId, contactPersonId);
            }
            if (isCallbackSuccess) {
                props.addCustomerToAppeal(appealId, customerId, contactPersonId);
                props.getCustomer({ customerId }, customerId);
                // contactPerson is fetched via CustomerPanel/ClientInfo component/module
                // if (contactPersonId) {
                //     props.getContactPerson({ id: contactPersonId }, customerId);
                // }
                props.getAppealFeedback({ appealId, customerId });
            }
            setIsLoading(false);
        }
        handleClose(true, true);
    }

    async function handleCustomerSave() {
        // touch and highlight the errors
        if (!isEmpty(errors)) {
            const formErrors = {};
            const formKeys = formColumns.flatMap(column => column.map(block => block.formKey));
            formKeys.forEach(key => {
                formErrors[key] = getFormSyncErrors(key)({ form });
            });
            Object.keys(formErrors).forEach(formKey => {
                const errors = formErrors[formKey];
                Object.keys(errors).forEach(fieldKey => {
                    touch(formKey, fieldKey);
                });
            });

            return;
        }
        setIsLoading(true);

        const formKeys = formColumns.flatMap(column => column.map(block => block.formKey));

        const formValues = {};
        formKeys.forEach(key => {
            const values = getFormValues(key)({ form });
            const keys = !isEmpty(values) ? Object.keys(values) : [];
            const filledValues = {};
            keys.forEach(key => {
                if (![null, undefined, ""].includes(values[key])) {
                    filledValues[key] = values[key];
                }
            });
            formValues[key] = filledValues;
        });

        try {
            // const params = getCustomerCreateParams(
            //     selectedCustomerType,
            //     formValues,
            //     newCustomerModalContext.customerId
            // );
            const paramsData = getCustomerCreateParamsData(
                selectedCustomerType,
                formValues,
                newCustomerModalContext.customerId
            );
            const { classId, partyType } = selectedCustomerType;
            const params = {
                data: paramsData,
                pathParams: { partyType, classId },
                jsonType: true,
            };
            if (classId) {
                params.queryParams = { classId };
            }
            const isContactPerson = selectedCustomerType.partyType === "contactPerson"; // specific case
            let urlKey = "create_customer";
            if (isContactPerson) {
                urlKey = "persons_save";
            }
            const response = await baseService.post(urlKey, params);

            if (response.success) {
                let customerId, personId;
                if (isContactPerson) {
                    customerId = params.data.customerId;
                    personId = response.result;
                } else {
                    customerId = response.result.customerId;
                    personId = response.result.personId;
                }

                let isCallbackSuccess = true; // set to true for cases without callback provided (is there such cases?)
                const callback = get(props.newCustomerModalContext, "callback");
                if (typeof callback === "function") {
                    isCallbackSuccess = await callback(customerId, personId);
                }

                const isOnlyContactPerson = get(props.newCustomerModalContext, "isOnlyContactPerson");
                // handle appeal related logic (could be definitely improved later)
                if (
                    !isOnlyContactPerson &&
                    isCallbackSuccess &&
                    get(props.newCustomerModalContext, "openNewTab") === false
                ) {
                    const path = props.location.pathname;
                    const splittedPath = path.split("/");
                    const appealsIndex = splittedPath.findIndex(item => item === "appeals");
                    const appealId = splittedPath[appealsIndex + 1];

                    props.addCustomerToAppeal(appealId, customerId, personId);
                    props.getCustomer({ customerId: customerId }, customerId);
                    // props.getContactPerson({ id: personId }, customerId);
                    props.getAppealFeedback({ appealId, customerId });
                }


                if (get(props.newCustomerModalContext, "openNewTab") === true) {
                    // if (isContactPerson || personId) {
                    if (personId) {
                        props.history.push(`/customer/${customerId}/person/${personId}/appeals`);
                    } else {
                        props.history.push(`/customer/${customerId}/appeals`);
                    }
                }
                setIsLoading(false);
                handleClose(true, true);
            } else {
                throw new Error("Customer save request error");
            }
        } catch (e) {
            console.error("CreateNewCustomerModal::handleAddressSave: Error", e);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isEmpty(debouncedPreviewValues)) {
            if (searchData && !searchData.isHidden) {
                setIsForceHiddenController(true);

                setTimeout(() => setIsForceHiddenController(false), MODAL_TRANSITION_DURATION);
                setSearchData(INIT_SEARCH_DATA);
            }
        }
    }, [debouncedPreviewValues]);

    useEffect(() => {
        setIsSomeFormDirty(false);
        setDebouncedPreviewValues({});
        if (searchData && !searchData.isHidden) {
            setSearchData(INIT_SEARCH_DATA);
        }
    }, [selectedCustomerType]);

    // aggregate errors and values from current columns
    useEffect(() => {
        if (formColumns) {
            const formKeys = formColumns.flatMap(column => column.map(block => block.formKey));
            if (!isSomeFormDirty) {
                const isSomeDirty = formKeys.find(key => isDirty(key)({ form }));
                if (isSomeDirty) {
                    setIsSomeFormDirty(true);
                }
            }
            let allValues = {};
            let allErrors = {};
            formKeys.forEach(key => {
                const values = getFormValues(key)({ form });
                const errors = getFormSyncErrors(key)({ form });
                allValues = { ...allValues, ...values };
                allErrors = { ...allErrors, ...errors };
            });
            setValues(allValues);
            setErrors(allErrors);
        }
    }, [form, formColumns]);

    useEffect(() => {
        if (!newCustomerModalContext.withoutTypeSelect) {
            fetchCustomerTypes();
        }
    }, []);

    return (
        <>
            <ModalWindow
                isOpen={isOpen}
                onClose={handleClose}
                className={cx("ordering-component-ui-core-wrapper create-new-customer-modal", {
                    "with-search-preview": isSearchPreview && !searchData.isHidden,
                })}
            >
                <div className="modal-wrapper" ref={modalRef}>
                    <header className="modal-window-header">
                        <div className="modal-window-title">{t(newCustomerModalContext.title) || t("newCustomer")}</div>
                    </header>
                    {(isLoading || isCustomerTypesLoading) && <Loader />}
                    <div className="create-new-customer-type">
                        {!isCustomerTypesLoading && !newCustomerModalContext.withoutTypeSelect && (
                            <DynamicForm
                                t={t}
                                formName={STATIC_TYPE_FORM_NAME}
                                dynData={initCustomerTypeForm}
                                isForceHiddenController={true}
                                withoutLiveSaver
                            />
                        )}
                    </div>
                    <div className="create-new-customer-form">
                        {selectedCustomerType && formColumns && formColumns.length > 0 && (
                            <CustomerForm
                                customerType={selectedCustomerType}
                                formColumns={formColumns}
                                isForceHiddenController={isForceHiddenController}
                                key={selectedCustomerType.id}
                            />
                        )}
                    </div>
                    {isSearchPreview && (
                        <SearchPreview
                            parentRef={modalRef && modalRef.current}
                            searchPreviewValues={debouncedPreviewValues}
                            filterSet={staticSearchPreviewBlock.filterSet}
                            onCardSelect={handleCardSelect}
                            onUpdate={handleSearchDataUpdate}
                            key={selectedCustomerType && selectedCustomerType.id}
                        />
                    )}
                    <footer className="modal-footer">
                        <button
                            type="submit"
                            className={cx("btn btn-primary", {
                                // disabled: !isEmpty(errors) || isSubmitDisabledBySearchPreview || isLoading,
                                disabled: isSubmitDisabledBySearchPreview || isLoading,
                            })}
                            // disabled={!isEmpty(errors) || isSubmitDisabledBySearchPreview || isLoading}
                            disabled={isSubmitDisabledBySearchPreview || isLoading}
                            onClick={handleCustomerSave}
                        >
                            {t("create")}
                        </button>
                    </footer>
                </div>
            </ModalWindow>
            {isWarningPopupOpen && (
                <ModalWindow
                    onClose={handleCloseWarningPopup}
                    className="modal-small ordering-component-ui-core-wrapper"
                >
                    <MessageModal
                        titleModal={t("saveWithoutChangeConfirm.title")}
                        contentModalText={t("modalWindowsLabels.notSavedWarning")}
                        dangerButton={true}
                        dangerButtonText={t("close")}
                        onClickDangerButton={() => handleClose(true)}
                        secondaryButton={true}
                        secondaryButtonText={t("cancel")}
                        onClickSecondaryButton={handleCloseWarningPopup}
                    />
                </ModalWindow>
            )}
        </>
    );
};

export default withRouter(withTranslation()(connect(mapStateToProps, mapDispatchToProps)(CreateNewCustomerModal)));
